import styled from "@emotion/styled";
import { Button, Table, Tag, Typography } from "antd";
import type { ColumnsType } from "antd/es/table";
import { FC, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { CreditTopupRow } from "services/factoryCreditApi";
import { formatDate, formatDateWithTime } from "utils/dateFormat";
import { numberFormatInt } from "utils/nuberFormat";
import { PAYMENT_TYPE_LABELS, STATUS_COLORS, STATUS_LABELS } from "./creditTopupOptions";
import { PaymentChannelIcon } from "./creditTopupIcons";

interface CreditTopupListTableProps {
  data: CreditTopupRow[];
  loading: boolean;
  total: number;
  page: number;
  limit: number;
  onPageChange: (page: number, pageSize: number) => void;
}

/**
 * 2026-09-03: แถวที่ยังรอชำระ (PENDING) และยังไม่หมดอายุ — โชว์ "คลิกเพื่อชำระเงิน" (ปุ่มสีน้ำเงิน ตัวอักษร
 * เล็กลง 2026-09-04) พร้อมเวลานับถอยหลังจริง (tick ทุกวินาที) ถึง qr_expires_at แทน Tag สถานะเฉยๆ
 * 2026-09-08: กดแล้ว navigate ไปหน้า checkout เต็มหน้า (CreditTopupCheckoutPage.tsx) แทนการเปิด
 * PaymentQrModal แบบเดิม (ยกเลิกใช้ modal นั้นแล้ว) — ส่ง package_version_id ของแถวนี้ผ่าน query string
 * ซึ่ง backend resume คืน QR เดิมให้เองถ้ายังไม่หมดอายุ (ดู docstring ของ initiateFactoryTopUp ฝั่ง API)
 */
const PendingCountdown: FC<{ expiresAt: string; onClick: () => void }> = ({ expiresAt, onClick }) => {
  const [remainingMs, setRemainingMs] = useState(() => new Date(expiresAt).getTime() - Date.now());

  useEffect(() => {
    const timer = window.setInterval(() => {
      setRemainingMs(new Date(expiresAt).getTime() - Date.now());
    }, 1000);
    return () => window.clearInterval(timer);
  }, [expiresAt]);

  const clampedSeconds = Math.max(0, Math.floor(remainingMs / 1000));
  const mm = String(Math.floor(clampedSeconds / 60)).padStart(2, "0");
  const ss = String(clampedSeconds % 60).padStart(2, "0");

  return (
    <div>
      <Button
        type="primary"
        size="small"
        onClick={onClick}
        style={{ backgroundColor: "#1677ff", borderColor: "#1677ff", fontSize: 12 }}
      >
        คลิกเพื่อชำระเงิน
      </Button>
      <Typography style={{ fontSize: 12, color: "#8c8c8c", marginTop: 4 }}>เหลือเวลา {mm}:{ss}</Typography>
    </div>
  );
};

const CreditTopupListTable: FC<CreditTopupListTableProps> = ({
  data,
  loading,
  total,
  page,
  limit,
  onPageChange,
}) => {
  const navigate = useNavigate();

  const goToCheckout = (packageVersionId: string | null) => {
    if (!packageVersionId) return;
    navigate(`/manage-credit/topup/checkout?packageVersionId=${packageVersionId}`);
  };

  // 2026-09-03: คอลัมน์ปรับใหม่ตามที่ขอ เรียงลำดับตามนี้เป๊ะ (ผู้ซื้อ/เบอร์โทร ย้ายมาไว้ก่อน "รายการ"
  // ตามรอบล่าสุด) — ชื่อผู้ทำรายการ (person_name) ถูกตัดออก แทนที่ด้วยชื่อผู้ซื้อ/เบอร์โทร
  // (buyer_name/buyer_phone — contact_name/contact_phone ดิบๆ)
  const columns: ColumnsType<CreditTopupRow> = [
    {
      title: "เลขที่คำสั่งซื้อ",
      dataIndex: "order_code",
      key: "order_code",
      width: 170,
      render: (v: string | null) => <Typography>{v || "—"}</Typography>,
    },
    {
      title: "วันที่สั่งซื้อ",
      dataIndex: "create_date",
      key: "create_date",
      width: 150,
      render: (v: string) => <Typography>{v ? formatDateWithTime(v) : "—"}</Typography>,
    },
    {
      title: "ชื่อผู้ซื้อ",
      dataIndex: "buyer_name",
      key: "buyer_name",
      width: 160,
      render: (v: string | null) => <Typography>{v || "—"}</Typography>,
    },
    {
      title: "เบอร์โทร",
      dataIndex: "buyer_phone",
      key: "buyer_phone",
      width: 130,
      render: (v: string | null) => <Typography>{v || "—"}</Typography>,
    },
    {
      // 2026-09-02: เดิมโชว์ชื่อ package + จำนวนเครดิตในวงเล็บ — ตามที่ขอ ให้เหลือแค่จำนวนเครดิตที่ได้รับ
      // เฉยๆ ไม่ต้องมีวงเล็บ (amount = ยอดเครดิตจริงที่รายการนี้ให้ อ้างอิงจาก transaction ตรงๆ แม่นกว่า
      // package_credit_amount เผื่อมีการ override ยอดตอนแอดมินเติมให้)
      title: "รายการ",
      dataIndex: "amount",
      key: "amount",
      width: 140,
      align: "right",
      render: (v: number) => <Typography>{`${numberFormatInt(Math.round(Number(v)))} เครดิต`}</Typography>,
    },
    {
      // บรรทัดที่ 1 = จำนวนวันคงเหลือ, บรรทัดที่ 2 = วันที่หมดอายุจริง — ตามที่ขอ (2026-09-02)
      title: "วันหมดอายุ",
      key: "lot_expires_at",
      width: 130,
      render: (_: unknown, row) => {
        if (row.lot_expires_at == null || row.lot_days_remaining == null) {
          return <Typography>—</Typography>;
        }
        return (
          <div>
            <Typography>{`เหลือ ${numberFormatInt(row.lot_days_remaining)} วัน`}</Typography>
            <Typography style={{ color: "#8c8c8c", fontSize: 12 }}>{formatDate(row.lot_expires_at)}</Typography>
          </div>
        );
      },
    },
    {
      title: "ประเภทการชำระเงิน",
      dataIndex: "payment_type",
      key: "payment_type",
      width: 160,
      render: (v: string | null) =>
        v ? (
          <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
            <PaymentChannelIcon type={v} style={{ fontSize: 16, color: "rgba(0,0,0,0.65)" }} />
            <Typography>{PAYMENT_TYPE_LABELS[v] ?? v}</Typography>
          </span>
        ) : (
          <Typography>—</Typography>
        ),
    },
    {
      title: "ราคา",
      dataIndex: "paid_amount",
      key: "paid_amount",
      width: 130,
      align: "right",
      render: (v: number | null) =>
        <Typography>{v != null ? Number(v).toLocaleString("th-TH", { minimumFractionDigits: 2 }) : "—"}</Typography>,
    },
    {
      title: "สถานะการชำระเงิน",
      dataIndex: "topup_status",
      key: "topup_status",
      width: 150,
      render: (v: string | null, row) => {
        // ยังรอชำระ + ยังไม่หมดอายุ (เทียบเวลาจริง ไม่ใช่แค่เชื่อ status ตรงๆ — เผื่อ cron ทุก 5 นาทีที่
        // mark EXPIRED ยังมาไม่ถึง) → โชว์ลิงก์ + นับถอยหลัง แทน Tag ปกติ
        if (v === "PENDING" && row.qr_expires_at && new Date(row.qr_expires_at).getTime() > Date.now()) {
          return (
            <PendingCountdown expiresAt={row.qr_expires_at} onClick={() => goToCheckout(row.package_version_id)} />
          );
        }
        return v ? <Tag color={STATUS_COLORS[v] ?? "default"}>{STATUS_LABELS[v] ?? v}</Tag> : <Typography>—</Typography>;
      },
    },
  ];

  return (
    <TableWrap>
      <Table<CreditTopupRow>
        rowKey="id"
        size="small"
        columns={columns}
        dataSource={data}
        loading={loading}
        scroll={{ x: true }}
        pagination={{
          current: page,
          pageSize: limit,
          total,
          showSizeChanger: true,
          pageSizeOptions: ["20", "50", "100"],
          onChange: onPageChange,
        }}
      />
    </TableWrap>
  );
};

export default CreditTopupListTable;

/** ให้เท่าตารางเมนูคำสั่งซื้อ (OrderTableList) — เหมือน CreditTransactionTable.tsx */
const TableWrap = styled.div`
  .ant-table-thead > tr > th {
    font-size: 12px !important;
    font-weight: 700;
  }

  .ant-table,
  .ant-table-tbody > tr > td,
  .ant-table-tbody > tr > td .ant-typography {
    font-size: 14px !important;
  }
`;
